// src/data/matrixReview.js
//
// RULING 310 (Bob 2026-10-13, "yes"): every agreement has its violation matrix in the General Chairperson's War Room; he
// confirms, corrects or strikes each entry, and he decides who sees it - nobody beyond himself, his Local Chairpersons, or
// all the members of his committee. Only entries he confirmed or corrected leave the War Room, each marked "confirmed by
// the General Chairperson on [date]", and they appear in the agreements library beside the agreement.
//
// The pieces (shared with functions\ by scripts\sync-shared-modules.js):
//   deckFromRows    - one agreement's matrix, built from data\YCP_VIOLATION_RULE_MAP.csv rows (scripts\publish-matrix-decks.js
//                     writes it to matrixDecks/{agreementKey}); nothing is typed in by hand.
//   newMatrixReview - the General Chairperson's ruling on one entry (matrixReviews, append-only; his last one governs).
//   newMatrixRelease- his choice of audience for one agreement (matrixReleases, append-only; the last one governs).
//   publishedView   - what may leave the War Room (the server writes it to matrixPublished/{agreementKey}).
// A quotation is the agreement's own words and is never edited here (PD-16): a correction is to the platform's words; a
// wrong quotation is struck.

export const DECISIONS = ['confirmed', 'corrected', 'struck'];
export const DECISION_WORDS = { confirmed: 'Confirmed', corrected: 'Corrected', struck: 'Struck' };
export const AUDIENCES = ['gc', 'lc', 'members'];
export const AUDIENCE_WORDS = {
  gc: 'Nobody beyond the General Chairperson',
  lc: 'The committee\'s Local Chairpersons',
  members: 'All members of the committee',
};
export const MAX_TEXT = 2000;
export const CONFIRMED_LINE = (date) => 'confirmed by the General Chairperson on ' + date;

const s = (v) => (v == null ? '' : String(v)).trim();

// FNV-1a, 32 bits, hex - the same id in the browser, the functions and the scripts.
function fnv(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return ('0000000' + h.toString(16)).slice(-8);
}

// An entry's id: stable while the entry's rule, type, line and quotation are unchanged. A changed quotation is a new entry
// (it needs a new ruling) - the old ruling stays with the old words.
export function entryId(row) {
  const v = s(row.violation_id || row.violationId), line = s(row.source_line || row.sourceLine);
  const key = [s(row.rule_as_printed || row.rule), v, line, s(row.quote_verbatim || row.quote)].join('|');
  return (v + '@' + line + '-' + fnv(key)).replace(/[^A-Za-z0-9_.@-]/g, '_');
}

// One agreement's matrix. `agreement` is { key, committee, title, base }; `rows` the rule-map rows for that key, in order;
// `taxonomy` maps violation_id -> taxonomy row (sub_label).
export function deckFromRows(agreement, rows, taxonomy, sourceSha) {
  const tax = taxonomy || {};
  // Two rows may quote the same words for the same type, differing only in how it is violated (two found 2026-10-13):
  // the second and later take -2, -3 ... in map order.
  const seen = {};
  const entries = (rows || []).map((r) => ({
    id: ((id) => { seen[id] = (seen[id] || 0) + 1; return seen[id] > 1 ? id + '-' + seen[id] : id; })(entryId(r)),
    rule: s(r.rule_as_printed),
    violationId: s(r.violation_id),
    label: s((tax[r.violation_id] || {}).sub_label) || s(r.violation_id),
    quote: String(r.quote_verbatim == null ? '' : r.quote_verbatim),
    sourceFile: s(r.source_file),
    sourceLine: s(r.source_line),
    how: s(r.how_violated),
    remedyCap: s(r.remedy_cap).toUpperCase() === 'Y',
    provenance: s(r.provenance),
  }));
  const rules = [];
  for (const e of entries) if (!rules.includes(e.rule)) rules.push(e.rule);
  return {
    agreementKey: agreement.key, committee: agreement.committee, title: s(agreement.title), base: s(agreement.base),
    rules, entries, entryCount: entries.length, sourceSha: s(sourceSha),
  };
}

// The General Chairperson's ruling on one entry.
export function newMatrixReview({ deck, entry, decision, label, how, note, user, serverTime }) {
  const errors = [];
  if (!deck || !deck.agreementKey || !deck.committee) errors.push('No matrix is open.');
  if (!entry || !entry.id) errors.push('No entry is selected.');
  if (!DECISIONS.includes(decision)) errors.push('Confirm, correct or strike.');
  if (!user || !user.uid) errors.push('You are not signed in.');
  const doc = {
    agreementKey: deck && deck.agreementKey, committee: deck && deck.committee, entryId: entry && entry.id,
    decision, note: s(note).slice(0, MAX_TEXT),
    by: user && user.uid, byName: s(user && user.name) || s(user && user.email), at: serverTime,
  };
  if (decision === 'corrected') {
    const L = s(label), H = s(how);
    if (!L && !H) errors.push('A correction says what the entry should read.');
    if (L.length > MAX_TEXT || H.length > MAX_TEXT) errors.push('A correction is at most ' + MAX_TEXT + ' characters.');
    doc.label = L || s(entry && entry.label);
    doc.how = H || s(entry && entry.how);
  }
  if (decision === 'struck' && !s(note)) errors.push('Say why the entry is struck.');
  return errors.length ? { ok: false, errors } : { ok: true, doc };
}

// His choice of audience for one agreement's matrix.
export function newMatrixRelease({ deck, audience, user, serverTime }) {
  const errors = [];
  if (!deck || !deck.agreementKey || !deck.committee) errors.push('No matrix is open.');
  if (!AUDIENCES.includes(audience)) errors.push('Choose who may see it.');
  if (!user || !user.uid) errors.push('You are not signed in.');
  if (errors.length) return { ok: false, errors };
  return { ok: true, doc: { agreementKey: deck.agreementKey, committee: deck.committee, audience,
    by: user.uid, byName: s(user.name) || s(user.email), at: serverTime } };
}

const ms = (t) => {
  if (!t) return 0;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000 + Math.floor((t.nanoseconds || 0) / 1e6);
  const n = new Date(t).getTime();
  return Number.isFinite(n) ? n : 0;
};
export const dateOf = (t) => { const n = ms(t); return n ? new Date(n).toISOString().slice(0, 10) : ''; };

// The last ruling on each entry governs; the earlier ones are kept but do not count.
export function currentRulings(reviews) {
  const out = {};
  for (const r of reviews || []) {
    if (!r || !r.entryId) continue;
    const cur = out[r.entryId];
    if (!cur || ms(r.at) >= ms(cur.at)) out[r.entryId] = r;
  }
  return out;
}
export function currentRelease(releases) {
  let cur = null;
  for (const r of releases || []) if (r && AUDIENCES.includes(r.audience) && (!cur || ms(r.at) >= ms(cur.at))) cur = r;
  return cur;
}

// Where the General Chairperson stands on a matrix: how many entries are confirmed, corrected, struck, still open.
export function reviewTally(deck, reviews) {
  const cur = currentRulings(reviews);
  const t = { confirmed: 0, corrected: 0, struck: 0, open: 0, total: 0 };
  for (const e of (deck && deck.entries) || []) {
    t.total++;
    const r = cur[e.id];
    if (r && DECISIONS.includes(r.decision)) t[r.decision]++; else t.open++;
  }
  return t;
}

// What may leave the War Room: only confirmed or corrected entries, and only when he released the matrix past himself.
// Corrected entries carry his words for the label and how; the quotation and line are always the agreement's.
export function publishedView(deck, reviews, releases) {
  const rel = currentRelease(releases);
  const audience = rel ? rel.audience : 'gc';
  const base = { agreementKey: deck && deck.agreementKey, committee: deck && deck.committee, title: s(deck && deck.title),
    base: s(deck && deck.base), audience, releasedBy: rel ? s(rel.byName) : '', releasedOn: rel ? dateOf(rel.at) : '' };
  if (!deck || audience === 'gc') return { ...base, entries: [] };
  const cur = currentRulings(reviews);
  const entries = [];
  for (const e of deck.entries || []) {
    const r = cur[e.id];
    if (!r || (r.decision !== 'confirmed' && r.decision !== 'corrected')) continue;
    const on = dateOf(r.at);
    entries.push({
      id: e.id, rule: e.rule, violationId: e.violationId, quote: e.quote, sourceLine: e.sourceLine, remedyCap: !!e.remedyCap,
      label: r.decision === 'corrected' && s(r.label) ? s(r.label) : e.label,
      how: r.decision === 'corrected' && s(r.how) ? s(r.how) : e.how,
      decision: r.decision, confirmedBy: s(r.byName), confirmedOn: on, confirmedLine: CONFIRMED_LINE(on),
    });
  }
  return { ...base, entries };
}
